import { GetServerSideProps } from 'next';
import { useEffect } from 'react';
import { CompanyType, OfferType, OrderType } from 'src/types';

import { faCircleExclamation } from '@fortawesome/sharp-solid-svg-icons';
import { Footer } from 'src/components/Checkout/Footer';
import { CheckoutHeader, CheckoutHeaderVariants } from 'src/components/Checkout/Header';
import { CheckoutOrderForm } from 'src/components/Checkout/OrderForm';
import { Icon } from 'src/components/ui/Icon';
import { Text } from 'src/components/ui/Text';
import { Title } from 'src/components/ui/Title';
import { useTracking } from 'src/providers/TrackingProvider';
import { getCurrentContract, getOffer, getOrder } from 'src/services/PlatformAPI';
import { AvailabilityTypes, ContractTypes } from 'src/utils/constants/types';

type OfferPageProps = {
  company: CompanyType;
  offer: OfferType;
  contractId: string;
  order: OrderType;
};

export default function OfferPage(props: OfferPageProps) {
  const { logEvent } = useTracking();
  const isOfferAvailable = props.offer.availability === AvailabilityTypes.ENABLED;
  const cachedOrder = props.order ? { ...props.order, id: null } : null;

  useEffect(() => {
    logEvent({
      event: 'viewed_offer',
      eventProperties: { offerId: props.offer.id },
    });
  }, []);

  return (
    <>
      <CheckoutHeader
        company={props.company}
        offer={props.offer}
        variant={CheckoutHeaderVariants.CHECKOUT}
        isDisabled={!isOfferAvailable}
        showBanner
      />

      <div className="mx-auto max-w-xl p-5 sm:px-0 sm:py-10">
        {isOfferAvailable && (
          <CheckoutOrderForm
            company={props.company}
            contractId={props.contractId}
            offer={props.offer}
            order={cachedOrder}
          />
        )}
        {!isOfferAvailable && (
          <div className="flex gap-4 rounded border border-foreground-subtle p-4">
            <Icon
              icon={faCircleExclamation}
              size="md"
              className="text-foreground-subtle"
            />

            <div className="flex flex-col gap-1">
              <Title
                size="sm"
                className="text-foreground-subtle"
              >
                Infelizmente esta oferta não está mais ativa
              </Title>
              <Text
                size="sm"
                className="text-foreground-subtle"
              >
                Fale com {props.company.name} para saber mais.
              </Text>
            </div>
          </div>
        )}

        <div className="my-8 h-px bg-background-least" />

        <Footer company={props.company} />
      </div>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const safeOfferId = ((context.query?.offer_id as string) || '').substring(0, 36);
  const orderId = context.query?.order_id as string;

  try {
    const [contract, offer, order] = await Promise.all([
      getCurrentContract(ContractTypes.SERVICE),
      getOffer(safeOfferId),
      orderId ? getOrder(orderId) : null,
    ]);

    const { company } = offer.product;

    context.res.setHeader('Cache-Control', 'public, max-age=300, stale-while-revalidate=3600');

    return {
      props: {
        company,
        contractId: contract.id,
        offer,
        order,
        palette: company.themeCustomizations.palette,
        pixelCode: offer.product.metaPixelCode,
        googlePixelCode: offer.product.googlePixelCode,
      },
    };
  } catch (e) {
    if (e?.response?.status === 404) return { notFound: true };

    throw e;
  }
};
